import type {
    AnchorHTMLAttributes,
    FC,
    MouseEvent,
    ReactNode
} from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { scrollToId } from '@/lib/smooth-scroll'

export interface SmartLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
    href: string
    children: ReactNode
}

const EXTERNAL = /^(https?:|mailto:|tel:)/

// One link for everything: in-page anchors glide to their section, other pages use Next routing,
// and outside links open safely in a new tab.
const SmartLink: FC<SmartLinkProps> = ({ href, onClick, children, ...rest }): ReactNode => {
    const router = useRouter()

    if (EXTERNAL.test(href)) {
        const opensNewTab = href.startsWith('http')

        return (
            <a
                href={href}
                onClick={onClick}
                {...(opensNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                {...rest}
            >
                {children}
            </a>
        )
    }

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event)

        if (event.defaultPrevented) return
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

        const [path, hash] = href.split('#')

        if (!hash) return
        if ((path || router.pathname) !== router.pathname) return

        if (scrollToId(hash, { focus: event.detail === 0 })) event.preventDefault()
    }

    return (
        <Link href={href} onClick={handleClick} {...rest}>
            {children}
        </Link>
    )
}

export default SmartLink
